import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { HubTooltip } from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';

const styles = {
	header: css({
		position: 'sticky',
		top: '0px',
		width: '100%',
		overflowX: 'hidden',
	}),
	headerButtons: css({
		paddingLeft: '0.25rem',
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		borderBottom: '1px solid #EEE',
		height: 33,
		minHeight: 33,
		overflowX: 'auto',
		marginLeft: '0.5rem',
	}),
	viewHeaderButton: (highlight: boolean) =>
		css(su.textXs, {
			color: highlight
				? theme.colors.text.primary
				: theme.colors.text.tableHeader,
			marginLeft: '0.75rem',
			marginRight: '0.75rem',
			fontWeight: highlight ? 'bold' : 'normal',
			textAlign: 'start',
		}),
};

export type TemplateSection =
	| 'template'
	| 'locations'
	| 'budgets'
	| 'audiences'
	| 'pages'
	| 'productCatalogs'
	| 'merchantCenters'
	| 'conversionGoals'
	| 'promotionalAssets'
	| 'history';

const VALID_TEMPLATE_SECTIONS: readonly TemplateSection[] = [
	'template',
	'locations',
	'budgets',
	'audiences',
	'pages',
	'productCatalogs',
	'merchantCenters',
	'conversionGoals',
	'promotionalAssets',
	'history',
];

export function parseTemplateSection(value: string | null): TemplateSection {
	if (
		value !== null &&
		(VALID_TEMPLATE_SECTIONS as readonly string[]).includes(value)
	) {
		return value as TemplateSection;
	}
	return 'template';
}

interface FacebookHeaderBarProps {
	section: TemplateSection;
	setSection: (section: TemplateSection) => void;
	sectionList?: TemplateSection[];
	uploadErrorsCount?: number;
}

export function FacebookHeaderBar({
	section,
	setSection,
	uploadErrorsCount,
	sectionList,
}: FacebookHeaderBarProps) {
	const { t } = useTranslation('facebook');

	const renderHeaderButton = (itemSection: TemplateSection) => {
		if (itemSection === 'history') return null;
		return (
			<button
				key={itemSection}
				type="button"
				onClick={() => setSection(itemSection)}
				css={styles.viewHeaderButton(itemSection === section)}
			>
				{t(`tab${itemSection.charAt(0).toUpperCase() + itemSection.slice(1)}`)}
			</button>
		);
	};

	return (
		<div css={styles.headerButtons}>
			<div>
				{sectionList?.map((itemSection) => renderHeaderButton(itemSection))}
				{sectionList?.includes('history') ? (
					<button
						type="button"
						onClick={() => setSection('history')}
						css={[
							styles.viewHeaderButton(section === 'history'),
							{ position: 'relative' },
						]}
					>
						{t('tabHistory')}
						{uploadErrorsCount ? (
							<HubTooltip title={uploadErrorsCount + ' upload errors'}>
								<span
									css={[
										{
											backgroundColor: theme.colors.palettes.error.main,
											color: theme.colors.white,
										},
										su.roundedFull,
										{
											paddingTop: '0px',
											paddingBottom: '0px',
											paddingLeft: '0.25rem',
											paddingRight: '0.25rem',
											position: 'absolute',
											top: '-10px',
											right: '-10px',
											fontSize: '10px',
										},
									]}
								>
									{uploadErrorsCount}
								</span>
							</HubTooltip>
						) : null}
					</button>
				) : null}
			</div>
		</div>
	);
}
